import React from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Container,
  Stack,
  Typography,
  Divider,
} from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Seo from '../SEO/Seo';

const Faq = ({ q, a }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 0.75 }}>
      {q}
    </Typography>
    <Typography variant="body1" color="text.secondary" sx={{ lineHeight: 1.7 }}>
      {a}
    </Typography>
  </Box>
);

const Support = () => {
  const origin = typeof window !== 'undefined' ? window.location.origin : 'https://rejectiontales.com';

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ContactPage',
    name: 'Rejection Tales Support',
    description: 'Help and support for Rejection Tales website and mobile apps.',
    url: `${origin}/support`,
  };

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 } }}>
      <Seo
        title="Support"
        description="Get help with Rejection Tales accounts, the mobile app, Luna, stories, and privacy requests."
        path="/support"
        schema={schema}
      />

      <Typography variant="h4" component="h1" fontWeight={800} sx={{ mb: 1 }}>
        Support
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3, lineHeight: 1.7 }}>
        We are here to help with account issues, the iOS app, stories, and product questions.
        Rejection Tales is a support product for everyday setbacks. It is not a crisis or emergency
        service.
      </Typography>

      <Card variant="outlined" sx={{ mb: 3, borderRadius: 3 }}>
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            Contact us
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2, lineHeight: 1.7 }}>
            For help with sign-in, account deletion, privacy requests, or bugs, reach out through
            our contact form. Include your account email and a short description of what happened.
          </Typography>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button component={RouterLink} to="/contact" variant="contained">
              Contact form
            </Button>
            <Button component={RouterLink} to="/privacy" variant="outlined">
              Privacy Policy
            </Button>
          </Stack>
        </CardContent>
      </Card>

      <Card
        variant="outlined"
        sx={{
          mb: 3,
          borderRadius: 3,
          borderColor: 'warning.light',
          backgroundColor: 'rgba(255, 193, 7, 0.06)',
        }}
      >
        <CardContent>
          <Typography variant="h6" fontWeight={700} sx={{ mb: 1 }}>
            If you are in crisis
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
            If you are in immediate danger or thinking about harming yourself, contact local
            emergency services or a crisis hotline right away. In the US, you can call or text 988
            (Suicide &amp; Crisis Lifeline). Rejection Tales and Luna cannot provide emergency care.
          </Typography>
        </CardContent>
      </Card>

      <Typography variant="h5" fontWeight={800} sx={{ mb: 2 }}>
        Common questions
      </Typography>

      <Faq
        q="How do I delete my account?"
        a="In the iOS app, open You (Account), then choose Delete account. This permanently removes your account and associated personal data from Rejection Tales, subject to limited legal or security retention."
      />
      <Faq
        q="Google sign-in is not working"
        a="Make sure you are using the latest app build, have a working internet connection, and try again. If it still fails, contact us with the exact error message you see."
      />
      <Faq
        q="How does Talk / microphone work?"
        a="Talk-to-text needs microphone and speech recognition permission on your device. Open Care or Luna, allow permissions when prompted, then tap the mic. Typing always works as a backup."
      />
      <Faq
        q="What is Luna?"
        a="Luna is an optional AI companion for empathy and reflection after a rejection. Replies are generated with AI providers. Luna is not a therapist, doctor, or crisis counselor."
      />
      <Faq
        q="How do daily reminders work?"
        a="Reminders are optional local notifications. You can enable them when creating an account or later in Care, and turn them off anytime in Care or iOS Settings."
      />
      <Faq
        q="Where is the Privacy Policy?"
        a="Read it at /privacy. It covers account data, stories, Luna, notifications, and deletion."
      />

      <Divider sx={{ my: 3 }} />

      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        Download the iOS app:{' '}
        <Box
          component="a"
          href="https://apps.apple.com/app/rejection-tales/id6813563793"
          target="_blank"
          rel="noopener noreferrer"
          sx={{ color: 'primary.main', fontWeight: 600 }}
        >
          Rejection Tales on the App Store
        </Box>
      </Typography>
      <Typography variant="body2" color="text.secondary">
        App Store support URL for Rejection Tales:{' '}
        <Box component="span" sx={{ fontFamily: 'monospace' }}>
          {origin}/support
        </Box>
      </Typography>
    </Container>
  );
};

export default Support;
